import { create } from 'zustand';
import { supabase } from '../supabase';
import { getCurrentHospitalId } from '../hospitalContext';

interface HospitalProfile {
  id?: string;
  logoUrl: string;
  name: string;
  phone1: string;
  phone2: string;
  phone3: string;
  email: string;
  address: string;
  website: string;
  description: string;
  usePrescriptionHeader?: boolean;
  wallpaperUrl?: string;
  wallpaperOpacity?: number;
  wallpaperUpdatedAt?: string;
}

interface HospitalProfileStore {
  profile: HospitalProfile;
  isLoading: boolean;
  error: string | null;
  fetchProfile: () => Promise<void>;
  updateProfile: (profile: Partial<HospitalProfile>) => Promise<void>;
  uploadWallpaper: (file: File) => Promise<string>;
  updateWallpaper: (wallpaperUrl: string, opacity?: number) => Promise<void>;
  removeWallpaper: () => Promise<void>;
}

const defaultProfile: HospitalProfile = {
  logoUrl: "",
  name: "",
  phone1: "",
  phone2: "",
  phone3: "",
  email: "",
  address: "",
  website: "",
  description: "",
  usePrescriptionHeader: true
};

export const useHospitalProfileStore = create<HospitalProfileStore>()((set, get) => ({
  profile: defaultProfile,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    set({ isLoading: true, error: null });
    
    // Retry logic for network errors (especially QUIC protocol errors)
    const fetchWithRetry = async (retries = 3): Promise<void> => {
    try {
      // Get current hospital ID for multi-tenant filtering
      const hospitalId = await getCurrentHospitalId();
      
      if (!hospitalId) {
        // Check if user is super admin - they don't need hospital profile
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          
          // Super admins don't have hospital_id - this is expected, don't warn
          if (profile?.role === 'super_admin') {
            set({ profile: defaultProfile, isLoading: false });
            return;
          }
        }
        
        // Only warn if not a super admin
        console.warn('No hospital ID found, using default profile');
        set({ profile: defaultProfile, isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('clinic_settings')
        .select('id, logo_url, name, phone, phone_secondary, phone_emergency, email, address, website, description, use_prescription_header, hospital_id, wallpaper_url, wallpaper_opacity, wallpaper_updated_at, created_at')
        .eq('hospital_id', hospitalId)
        .order('created_at', { ascending: false })
        .limit(1);

        // Handle Supabase errors
      if (error) {
          // Check if it's a network error that might be transient
          const isNetworkError = error.message?.includes('Failed to fetch') || 
                                 error.message?.includes('QUIC') ||
                                 error.message?.includes('network');
          
          if (isNetworkError && retries > 0) {
            // Retry after a short delay
            await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
            return fetchWithRetry(retries - 1);
          }
          
          console.error('Error fetching clinic settings:', error);
          // Don't throw - fall through to use default profile
        } else if (data && data.length > 0) {
        const profileData = data[0]; // Get the first (most recent) profile
        // Map database fields to our interface
        const mappedProfile: HospitalProfile = {
          id: profileData.id,
          logoUrl: profileData.logo_url || "",
          name: profileData.name || defaultProfile.name,
          phone1: profileData.phone || defaultProfile.phone1,
          phone2: profileData.phone_secondary || "",
          phone3: profileData.phone_emergency || "",
          email: profileData.email || defaultProfile.email,
          address: profileData.address || defaultProfile.address,
          website: profileData.website || defaultProfile.website,
          description: profileData.description || defaultProfile.description,
          usePrescriptionHeader: profileData.use_prescription_header ?? true,
          // Wallpaper fields
          wallpaperUrl: profileData.wallpaper_url || undefined,
          wallpaperOpacity: profileData.wallpaper_opacity || undefined,
          wallpaperUpdatedAt: profileData.wallpaper_updated_at || undefined
        };
        set({ profile: mappedProfile, isLoading: false });
          return;
        }
        
        // No profile exists or error occurred, use defaults (non-blocking)
        set({ profile: defaultProfile, isLoading: false });
      } catch (error: any) {
        // Handle network errors (QUIC, fetch failures, etc.)
        const isNetworkError = error?.message?.includes('Failed to fetch') || 
                               error?.message?.includes('QUIC') ||
                               error?.message?.includes('network') ||
                               error?.name === 'TypeError';
        
        if (isNetworkError && retries > 0) {
          // Retry after a short delay
          await new Promise(resolve => setTimeout(resolve, 1000 * (4 - retries)));
          return fetchWithRetry(retries - 1);
        }
        
        // Log error but don't break the app - use default profile
        console.warn('Error fetching clinic profile (using defaults):', error?.message || error);
      set({ 
          error: null, // Don't show error to user for network issues
        isLoading: false,
        profile: defaultProfile 
      });
    }
    };
    
    await fetchWithRetry();
  },

  updateProfile: async (newProfile) => {
    set({ isLoading: true, error: null });
    try {
      // Get current hospital ID for multi-tenant support
      const hospitalId = await getCurrentHospitalId();
      
      if (!hospitalId) {
        throw new Error('No hospital ID found. Please login again.');
      }

      const currentProfile = get().profile;
      
      // Prepare data for database (map our interface to DB fields)
      const dbData = {
        name: newProfile.name || currentProfile.name,
        address: newProfile.address || currentProfile.address,
        phone: newProfile.phone1 || currentProfile.phone1,
        phone_secondary: newProfile.phone2 ?? currentProfile.phone2 ?? null,
        phone_emergency: newProfile.phone3 ?? currentProfile.phone3 ?? null,
        email: newProfile.email || currentProfile.email,
        website: newProfile.website || currentProfile.website,
        description: newProfile.description || currentProfile.description,
        logo_url: newProfile.logoUrl || currentProfile.logoUrl,
        use_prescription_header: newProfile.usePrescriptionHeader ?? currentProfile.usePrescriptionHeader ?? true,
        hospital_id: hospitalId,
        updated_at: new Date().toISOString()
      } as any;

      if (currentProfile.id) {
        // Update existing profile
        const { data, error } = await supabase
          .from('clinic_settings')
          .update(dbData)
          .eq('id', currentProfile.id)
          .eq('hospital_id', hospitalId) // Ensure hospital isolation
          .select()
          .single();

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { ...state.profile, ...newProfile, id: data.id },
          isLoading: false
        }));
      } else {
        // Create new profile
        const { data, error } = await supabase
          .from('clinic_settings')
          .insert([dbData])
          .select()
          .single();

        if (error) throw error;

        // Update local state
        set((state) => ({
          profile: { ...state.profile, ...newProfile, id: data.id },
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Error updating clinic profile:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
    }
  },

  uploadWallpaper: async (file: File) => {
    set({ isLoading: true, error: null });
    try {
      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }
      
      if (!allowedTypes.includes(file.type)) {
        throw new Error('File must be a JPEG, PNG, or WebP image');
      }

      // Get current hospital ID for consistent filename (one wallpaper per hospital)
      const hospitalId = await getCurrentHospitalId();
      if (!hospitalId) {
        throw new Error('No hospital ID found. Please login again.');
      }

      // Get current profile to check for existing wallpaper
      const currentProfile = get().profile;
      
      // Delete old wallpaper from storage if it exists (only one wallpaper per hospital)
      if (currentProfile.wallpaperUrl) {
        try {
          // Extract filename from URL
          const oldFileName = currentProfile.wallpaperUrl.split('/').pop();
          if (oldFileName) {
            console.log('Deleting old wallpaper:', oldFileName);
            const { error: deleteError } = await supabase.storage
              .from('wallpapers')
              .remove([oldFileName]);
            
            if (deleteError) {
              console.warn('Error deleting old wallpaper (non-critical):', deleteError);
              // Continue with upload even if deletion fails
            }
          }
        } catch (deleteErr) {
          console.warn('Error deleting old wallpaper (non-critical):', deleteErr);
          // Continue with upload
        }
      }

      // Generate consistent filename per hospital (replaces old one automatically)
      const fileExt = file.name.split('.').pop();
      const fileName = `wallpaper-${hospitalId}.${fileExt}`;

      console.log('Uploading file:', {
        name: fileName,
        type: file.type,
        size: file.size
      });

      // Check for active session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please login again.');
      }

      // Determine correct MIME type from file extension
      const mimeTypes: Record<string, string> = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'webp': 'image/webp'
      };
      const fileType = mimeTypes[fileExt?.toLowerCase() || ''] || 'image/jpeg';

      console.log('Original file:', {
        name: file.name,
        type: file.type,
        size: file.size,
        ext: fileExt
      });
      console.log('Determined MIME type:', fileType);

      // Read file as ArrayBuffer to ensure we have raw binary data
      const arrayBuffer = await file.arrayBuffer();
      
      // Create a Blob with explicit content type
      const blob = new Blob([arrayBuffer], { type: fileType });
      
      // Create a new File from the Blob with explicit type
      const fileToUpload = new File([blob], fileName, { 
        type: fileType,
        lastModified: Date.now()
      });

      console.log('File to upload:', {
        name: fileToUpload.name,
        type: fileToUpload.type,
        size: fileToUpload.size
      });

      // Upload using Supabase Storage API
      // Use upsert: true to replace existing file with same name (one wallpaper per hospital)
      const { data, error } = await supabase.storage
        .from('wallpapers')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: true // Replace existing wallpaper for this hospital
        });

      if (error) {
        console.error('Upload error:', error);
        // If the error is still about MIME type, try using the original file directly
        if (error.message?.includes('mime type') || error.message?.includes('invalid_mime_type')) {
          console.log('Retrying with original file...');
          const { data: retryData, error: retryError } = await supabase.storage
            .from('wallpapers')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false
            });
          
          if (retryError) {
            throw new Error(`Upload failed: ${retryError.message}`);
          }
          
          return supabase.storage
            .from('wallpapers')
            .getPublicUrl(fileName).data.publicUrl;
        }
        throw new Error(`Upload failed: ${error.message}`);
      }

      console.log('Upload successful:', data);

      // Get public URL using Supabase Storage API
      const { data: { publicUrl } } = supabase.storage
        .from('wallpapers')
        .getPublicUrl(fileName);

      console.log('Public URL:', publicUrl);

      set({ isLoading: false });
      return publicUrl;
    } catch (error) {
      console.error('Error uploading wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  },

  updateWallpaper: async (wallpaperUrl: string, opacity: number = 0.2) => {
    set({ isLoading: true, error: null });
    try {
      const currentProfile = get().profile;
      
      if (!currentProfile.id) {
        throw new Error('No profile found. Please create a profile first.');
      }

      // Prepare wallpaper data
      // NOTE: You must run the migration first: supabase/migrations/20241206000003_add_wallpaper_fields_to_clinic_settings.sql
      const dbData = {
        wallpaper_url: wallpaperUrl,
        wallpaper_opacity: opacity,
        wallpaper_updated_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Update existing profile
      const { error } = await supabase
        .from('clinic_settings')
        .update(dbData)
        .eq('id', currentProfile.id);

      if (error) {
        // Check if error is about missing columns
        if (error.message?.includes('wallpaper') || error.code === 'PGRST204') {
          throw new Error(
            'Wallpaper columns not found in database. Please run the migration:\n\n' +
            '1. Go to Supabase Dashboard → SQL Editor\n' +
            '2. Run the migration: supabase/migrations/20241206000003_add_wallpaper_fields_to_clinic_settings.sql\n' +
            '3. Then try uploading the wallpaper again.'
          );
        }
        throw error;
      }

      // Update local state
      set((state) => ({
        profile: { 
          ...state.profile, 
          wallpaperUrl, 
          wallpaperOpacity: opacity,
          wallpaperUpdatedAt: dbData.wallpaper_updated_at
        },
        isLoading: false
      }));
    } catch (error) {
      console.error('Error updating wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  },

  removeWallpaper: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentProfile = get().profile;
      
      if (!currentProfile.id) {
        throw new Error('No profile found. Please create a profile first.');
      }

      // Delete wallpaper file from storage if it exists
      if (currentProfile.wallpaperUrl) {
        try {
          // Extract filename from URL
          const fileName = currentProfile.wallpaperUrl.split('/').pop();
          if (fileName) {
            console.log('Deleting wallpaper file from storage:', fileName);
            const { error: deleteError } = await supabase.storage
              .from('wallpapers')
              .remove([fileName]);
            
            if (deleteError) {
              console.warn('Error deleting wallpaper file (non-critical):', deleteError);
              // Continue with database update even if file deletion fails
            } else {
              console.log('Wallpaper file deleted successfully');
            }
          }
        } catch (deleteErr) {
          console.warn('Error deleting wallpaper file (non-critical):', deleteErr);
          // Continue with database update
        }
      }

      // Update database to remove wallpaper reference
      const dbData = {
        wallpaper_url: null,
        wallpaper_opacity: null,
        wallpaper_updated_at: null,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('clinic_settings')
        .update(dbData)
        .eq('id', currentProfile.id);

      if (error) throw error;

      // Update local state
      set((state) => ({
        profile: { 
          ...state.profile, 
          wallpaperUrl: undefined, 
          wallpaperOpacity: undefined,
          wallpaperUpdatedAt: undefined
        },
        isLoading: false
      }));
    } catch (error) {
      console.error('Error removing wallpaper:', error);
      set({ 
        error: (error as Error).message, 
        isLoading: false 
      });
      throw error;
    }
  }
})); 